import React, { FC } from 'react';

import Icon from 'src/common/components/IconDS22';
import useFeatureFlag from 'src/common/hooks/useFeatureFlag';
import getClassName from 'src/common/utils/getClassName';

import useMediaQuery, { sharedMediaQueries } from 'src/common/hooks/useMediaQuery';
import NavDropdown from '../NavDropdown';
import SubNavigation from './SubNavigation';

import s from './Navigation.module.scss';
import UnauthenticatedNavItems from './UnauthenticatedNavItems';
import { ImmutableUser } from '../../../types/user/User';
import configItems, { type NestedNavigationConfigType } from './config';
import NavItem from './NavItem';
import NavModal from '../NavModal';
import { WithLabel } from '../NestedMenu';
import ScreenSizeWrapper from './ScreenSizeWrapper';

type Props = {
  user: ImmutableUser;
  classNameModifier?: string;
};
const Navigation: FC<Props> = ({ classNameModifier = '', user }) => {
  const navUpdateEnabled = useFeatureFlag('pricing-v2--nav');
  const isMediumAndUpScreenSize = useMediaQuery(sharedMediaQueries.m);
  const isLoggedIn = user.get('loggedIn');
  const isOwner = user.get('owner_approved');
  const isCaptain = user.get('is_captain');
  const modifiers = [classNameModifier, 'mainNav', isLoggedIn ? 'authenticated' : 'anonymous'];
  const modifiersString = modifiers.filter(Boolean).join(' ');

  let menuItems = isCaptain
    ? configItems.filter(
      (config) => config.isHeaderNavItem?.owner && (config.roles == null || config.roles.includes('captain')),
    )
    : configItems.filter(
      (config) => config.isHeaderNavItem?.renter && (config.roles == null || config.roles.includes('renter')),
    );

  if (isOwner) {
    menuItems = configItems.filter((config) => {
      // Remove Admin nav item when nav update is enabled, or remove My Listings when disabled
      if (
        (navUpdateEnabled && config.key === 'admin')
        || (!navUpdateEnabled && config.key === 'my-listings')
      ) {
        return false;
      }
      return config.isHeaderNavItem?.owner && (config.roles == null || config.roles.includes('owner'));
    });
  }

  return (
    <nav className={getClassName(s, 'root', classNameModifier)}>
      <ul className={getClassName(s, 'menu', classNameModifier)}>
        {isLoggedIn && menuItems.map((data: NestedNavigationConfigType) => {
          const screenSizeModifiers = data.isHeaderNavItem?.[isOwner ? 'owner' : 'renter']?.join(' ');
          const iconOnlyScreenSizeModifiers = data.iconOnlyScreenSizes?.[isOwner ? 'owner' : 'renter']?.map((size) => `iconOnly_${size}`).join(' ');
          if (!screenSizeModifiers) return null;
          const asNestedMenuItem = data as WithLabel;
          // Remove icon property from each option if screen size is medium and up
          const options = (data.options?.map(
            (item) => (isMediumAndUpScreenSize
              ? ({ ...item, icon: undefined })
              : item),
          ));

          return (
            <NavItem
              key={data.key}
              classNameModifier={`${iconOnlyScreenSizeModifiers} ${modifiersString} ${screenSizeModifiers}`}
              to={asNestedMenuItem.to}
              options={options}
              icon={asNestedMenuItem.icon}
              openInNewTab={data.openInNewTab}
            >
              {asNestedMenuItem.label}
            </NavItem>
          );
        })}

        {!isLoggedIn && <UnauthenticatedNavItems classNameModifier={modifiersString} />}

        <li
          className={getClassName(
            s,
            'item',
            [...modifiers, 'dropdown'].filter(Boolean).join(' '),
          )}
        >
          <ScreenSizeWrapper screenSizeWrapper="mDown">
            <NavModal
              classNameModifier="menuItems nestedMenu"
              heading={(
                isLoggedIn && (
                  <Icon
                    id="menu"
                    size="l"
                    className={s.hamburger}
                  />
                )
              )}
            >
              <SubNavigation nestedMenuVariant="sidebar" />
            </NavModal>
          </ScreenSizeWrapper>
          <ScreenSizeWrapper screenSizeWrapper="m">
            <NavDropdown
              className={getClassName(s, 'link', modifiersString)}
              classNameModifier="nestedMenu"
              data-test="MainMenuDropDown"
              aria-label="Main menu"
              withChevron={false}
              heading={(
                isLoggedIn && (
                  <Icon
                    id="menu"
                    size="l"
                    className={s.hamburger}
                  />
                )
              )}
            >
              <SubNavigation />
            </NavDropdown>
          </ScreenSizeWrapper>
        </li>
      </ul>
    </nav>
  );
};

export default Navigation;
